import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const BillingContext = createContext(null);

const ENTRY_FEATURES = new Set(["musings", "team_db", "customers_db", "scheduler"]);
const EXEC_FEATURES = new Set([
  "musings", "ai_studio", "vision_pulse", "personal_notes",
  "team_db", "customers_db", "scheduler", "prompts",
]);

function planAllowsFeature(plan, feature) {
  if (plan === "enterprise" || plan === "executive") return EXEC_FEATURES.has(feature);
  return ENTRY_FEATURES.has(feature);
}

export function BillingProvider({ children }) {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [upgradeReason, setUpgradeReason] = useState(null); // {message, feature, plan}

  const refresh = useCallback(async () => {
    if (!user) { setInfo(null); return; }
    try { const { data } = await api.get("/billing/me"); setInfo(data); } catch {}
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const plan = info?.plan || user?.effective_plan || "entry";
  const seatFeatures = user?.seat_features || {};
  const isOrgMember = !!user?.is_org_member;
  const isOrgAdmin = !!user?.is_org_admin;

  // Returns true if user can access feature
  const canUse = useCallback((feature) => {
    if (!planAllowsFeature(plan, feature)) return false;
    if (isOrgMember && seatFeatures[feature] === false) return false;
    return true;
  }, [plan, isOrgMember, seatFeatures]);

  // Show upgrade modal with detail
  const requireUpgrade = useCallback((detail) => setUpgradeReason(detail || { message: "Upgrade required" }), []);
  const dismissUpgrade = useCallback(() => setUpgradeReason(null), []);

  // Helper: intercept 402/403 errors and pop upgrade modal
  const handleApiError = useCallback((err) => {
    const detail = err?.response?.data?.detail;
    const status = err?.response?.status;
    if (status === 402 || (status === 403 && detail?.code === "seat_feature_disabled")) {
      const msg = typeof detail === "string" ? detail : detail?.message || "Upgrade required";
      setUpgradeReason({ message: msg, feature: detail?.feature, plan: detail?.plan, code: detail?.code });
      return true;
    }
    return false;
  }, []);

  return (
    <BillingContext.Provider value={{
      info, plan, seatFeatures, isOrgMember, isOrgAdmin,
      canUse, refresh, requireUpgrade, dismissUpgrade, upgradeReason,
      handleApiError,
    }}>
      {children}
    </BillingContext.Provider>
  );
}

export const useBilling = () => useContext(BillingContext) || {
  info: null, plan: "entry", seatFeatures: {}, isOrgMember: false, isOrgAdmin: false,
  canUse: () => true, refresh: () => {}, requireUpgrade: () => {}, dismissUpgrade: () => {},
  upgradeReason: null, handleApiError: () => false,
};
